'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useStore } from 'zustand';

import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import type { BooleanFeatureName } from '@/feature-flags/consts/features.const';
import { authStore } from '@/store/auth.store';
import { logInfo } from '@/feature-flags/utils/logger';
import { useRequireAuthentication } from '@/hooks/use-require-authentication.hook';
import { getCurrentEvaluationId } from '@/feature-flags/utils/correlation';
import { getCurrentRouteContext } from '@/feature-flags/utils/route-context';

export type FeatureGuardDeniedAction = 'redirect' | 'notFound';

export interface UseFeatureGuardOptions {
  requireAuth?: boolean;
  onDenied?: FeatureGuardDeniedAction;
  redirectTarget?: string;
}

export interface UseFeatureGuardResult {
  granted: boolean;
  loading: boolean;
}

export function useFeatureGuard(
  feature: BooleanFeatureName,
  options: UseFeatureGuardOptions = {},
): UseFeatureGuardResult {
  const { requireAuth = true, onDenied = 'redirect', redirectTarget = '/' } = options;

  const router = useRouter();
  const { value, loading: valueLoading, isFallback, variant, error } = useFeatureFlag(feature);

  const authStatus = useStore(authStore, (state) => state.authStatus);
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);
  const userId = useStore(authStore, (state) => state.authenticatedUser?.id);
  const isAuthReady = authStatus !== 'loading';

  const hasInitialEvaluationRef = useRef(false);

  const granted = requireAuth ? value && isLoggedIn : value;
  const loading = requireAuth ? valueLoading || !isAuthReady : valueLoading;
  const isAuthenticationMissing = requireAuth && isAuthReady && !isLoggedIn && !valueLoading && value;

  useRequireAuthentication(isAuthenticationMissing);

  useEffect(() => {
    const hasRealVariant = variant !== null && variant !== 'no-match-fallback';
    const hasFallbackWithUserId = variant === 'no-match-fallback' && !!userId;
    const hasRequiredUserId = isLoggedIn ? !!userId : true;
    const hasTerminalError = !!error && !valueLoading;
    const hasResolvedOutcome = hasRealVariant || hasFallbackWithUserId || hasTerminalError;

    const isRealEvaluation = requireAuth
      ? !valueLoading && isAuthReady && hasRequiredUserId && hasResolvedOutcome
      : !valueLoading;

    if (!hasInitialEvaluationRef.current && isRealEvaluation) {
      hasInitialEvaluationRef.current = true;
    }
  }, [requireAuth, valueLoading, variant, isFallback, isAuthReady, userId, isLoggedIn, error]);

  useEffect(() => {
    if (loading || granted || onDenied !== 'redirect' || isAuthenticationMissing) {
      return;
    }

    if (!hasInitialEvaluationRef.current) {
      return;
    }

    const routeContext = getCurrentRouteContext();

    logInfo('Feature access denied, redirecting user', {
      feature_flag: {
        event: 'feature_redirect_triggered',
        evaluation_id: getCurrentEvaluationId(),
        feature,
        route: routeContext.route,
        redirect_target: redirectTarget,
        source: 'useFeatureGuard',
        variant,
        is_fallback: isFallback,
      },
      ...(userId ? { user: { id: userId } } : {}),
    });

    router.replace(redirectTarget);
  }, [
    loading,
    granted,
    onDenied,
    redirectTarget,
    feature,
    userId,
    router,
    variant,
    isFallback,
    isAuthenticationMissing,
  ]);

  return { granted, loading };
}
